/**
 * Explorer Test Page
 * Dedicated page for testing consensus_flat WebSocket subscriptions
 * Displays flattened consensus events from Morpheum's DAG-based consensus system
 * Features: Timeline view and Graph view for DAG visualization
 */

import { useState, useEffect, useRef, useMemo, Suspense } from "react";
import { SubscriptionExplorer, SubscriptionClient, MultiplexStreamClient } from "../sdk";
import type { MultiplexStreamSubscription } from "../sdk/types/api";
import {
  DEFAULT_WS_URL,
  DEFAULT_DEVICE_ID,
} from "../constants";
import { ConsensusEventFlat, TransactionEvent } from "../sdk/types/api";
import { ConnectionState } from "../sdk/types/transport";
import { ExplorerButton } from "./explorer_test_page/ExplorerTestPageButtons";
import { ConfigSection } from "./explorer_test_page/ConfigSection";
import { ConsensusEventList } from "./explorer_test_page/ConsensusEventList";
import {
  createDagState,
  clearDagState,
  batchUpdates,
  buildTimelineData,
  buildGraphData,
  type TxItem,
  type DagState,
  cleanupOrphans,
} from "../utils/dagUtils";
import { Timeline } from "vis-timeline/standalone";
import "vis-timeline/styles/vis-timeline-graph2d.min.css";
import ForceGraph2D from "react-force-graph-2d";
import "./explorer_test_page/explorertest.css";

export interface ConsensusEventMessage {
  id: number;
  timestamp: number;
  event: ConsensusEventFlat | TransactionEvent;
  type: "success" | "error" | "info" | "warning" | "debug";
  eventSource?: "consensus_flat" | "transactions";
}

export default function ExplorerTestPage() {
  const [wsURL, setWsURL] = useState(DEFAULT_WS_URL);
  const [authToken, setAuthToken] = useState(() => {
    return localStorage.getItem("authToken") || "";
  });
  const [deviceID, setDeviceID] = useState(DEFAULT_DEVICE_ID);
  const [shardId, setShardId] = useState<string>("");
  const [eventType, setEventType] = useState<string>("");
  const [isConnected, setIsConnected] = useState(false);
  const [events, setEvents] = useState<ConsensusEventMessage[]>([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribedTransactions, setIsSubscribedTransactions] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Transaction subscription filter parameters
  const [txAddress, setTxAddress] = useState<string>("");
  const [txHash, setTxHash] = useState<string>("");
  const [txTypes, setTxTypes] = useState<string>("");
  const [txEventTypes, setTxEventTypes] = useState<string>("");

  // DAG visualization state
  const [viewMode, setViewMode] = useState<"timeline" | "graph" | "list">("list");
  const [dagState, setDagState] = useState<DagState>(() => createDagState());
  const [dagNodes, setDagNodes] = useState<TxItem[]>([]);
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);

  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const subscriptionExplorerRef = useRef<SubscriptionExplorer | null>(null);
  const multiplexStreamClientRef = useRef<MultiplexStreamClient | null>(null);
  const multiplexTxUnsubRef = useRef<(() => void) | null>(null);
  const multiplexTxSubsRef = useRef<MultiplexStreamSubscription[] | null>(null);
  const messageIdRef = useRef(0);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const timelineInstanceRef = useRef<Timeline | null>(null);
  const orphanCleanupIntervalRef = useRef<number | null>(null);
  const wasDisconnectedRef = useRef(false);

  const addEvent = (
    event: ConsensusEventFlat | TransactionEvent,
    type: "success" | "error" | "info" | "warning" | "debug" = "success",
    eventSource: "consensus_flat" | "transactions" = "consensus_flat",
  ) => {
    const newEvent: ConsensusEventMessage = {
      id: messageIdRef.current++,
      timestamp: Date.now(),
      event,
      type,
      eventSource,
    };
    setEvents((prev) => [newEvent, ...prev].slice(0, 200)); // Keep last 200 events
  };

  // Track processed event IDs to avoid reprocessing
  const processedEventIdsRef = useRef<Set<number>>(new Set());

  // Process events for DAG visualization
  useEffect(() => {
    if (events.length === 0) return;

    // Only process new events that haven't been processed yet
    const newEvents = events.filter((e) => !processedEventIdsRef.current.has(e.id));
    if (newEvents.length === 0) return;

    // Mark events as processed
    newEvents.forEach((e) => processedEventIdsRef.current.add(e.id));

    // Filter to only ConsensusEventFlat events (TransactionEvent has different structure)
    const consensusEvents = newEvents
      .map((e) => e.event)
      .filter((event): event is ConsensusEventFlat => {
        // ConsensusEventFlat has data with 'type' field (ConsensusItemFlat)
        // TransactionEvent has data with 'hash' or 'txhash' field (TransactionEventData)
        return (
          "data" in event &&
          event.data &&
          typeof event.data === "object" &&
          "type" in event.data
        );
      });
    const currentState = dagState;

    // Only process ConsensusEventFlat events for DAG visualization
    if (consensusEvents.length > 0) {
      batchUpdates(consensusEvents, currentState, (processed: TxItem[]) => {
        setDagNodes((prev) => {
          // Merge and deduplicate by ID
          const merged = [...prev, ...processed];
          const unique = merged.filter(
            (tx, idx, arr) => arr.findIndex((t) => t.id === tx.id) === idx,
          );

          // Prune to last 5000 items OR last 2 hours
          const twoHoursAgo = Date.now() / 1000 - 7200;
          const filtered = unique.filter((tx) => tx.timestamp >= twoHoursAgo);
          return filtered.length > 5000 ? filtered.slice(-5000) : filtered;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]); // Only depend on events, dagState is stable (mutable ref pattern)

  // Cleanup DAG state on unmount
  useEffect(() => {
    return () => {
      clearDagState(dagState);
      if (timelineInstanceRef.current) {
        timelineInstanceRef.current.destroy();
        timelineInstanceRef.current = null;
      }
      if (orphanCleanupIntervalRef.current) {
        clearInterval(orphanCleanupIntervalRef.current);
        orphanCleanupIntervalRef.current = null;
      }
      processedEventIdsRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on unmount

  // Periodic orphan cleanup
  useEffect(() => {
    orphanCleanupIntervalRef.current = window.setInterval(() => {
      cleanupOrphans(dagState, 30000);
    }, 30000); // Every 30 seconds

    return () => {
      if (orphanCleanupIntervalRef.current) {
        clearInterval(orphanCleanupIntervalRef.current);
        orphanCleanupIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount, dagState is stable

  const connect = async () => {
    if (isConnecting) {
      return;
    }

    setIsConnecting(true);
    addEvent(
      {
        eventType: "info",
        data: {
          id: "connection",
          type: "vertex",
          shardId: 0,
          timestamp: Math.floor(Date.now() / 1000),
          status: "connecting",
          originalProtoHash: "",
        },
        sourceShard: "system",
        eventTime: Math.floor(Date.now() / 1000),
      },
      "info",
    );

    // Validate URL format
    try {
      const url = new URL(wsURL);
      if (url.protocol !== "ws:" && url.protocol !== "wss:") {
        throw new Error("URL must use ws:// or wss:// protocol");
      }
    } catch (error: any) {
      addEvent(
        {
          eventType: "error",
          data: {
            id: "connection-error",
            type: "vertex",
            shardId: 0,
            timestamp: Math.floor(Date.now() / 1000),
            status: "error",
            originalProtoHash: "",
          },
          sourceShard: "system",
          eventTime: Math.floor(Date.now() / 1000),
        },
        "error",
      );
      setIsConnecting(false);
      return;
    }

    // Cleanup existing connections
    if (subscriptionExplorerRef.current) {
      try {
        subscriptionExplorerRef.current.disconnect();
      } catch (error) {
        // Ignore cleanup errors
      }
    }
    if (subscriptionClientRef.current) {
      try {
        subscriptionClientRef.current.disconnect();
      } catch (error) {
        // Ignore cleanup errors
      }
    }

    // Create or reuse SubscriptionClient to share WebSocketClient
    try {
      // Create SubscriptionClient if it doesn't exist
      if (!subscriptionClientRef.current) {
        subscriptionClientRef.current = new SubscriptionClient({
          url: wsURL,
          authToken: authToken || undefined,
          walletAddress: undefined,
          deviceID: deviceID || undefined,
        });
      }

      // Create SubscriptionExplorer with shared WebSocketClient BEFORE connecting
      // This allows us to set up state listeners before connection state changes
      subscriptionExplorerRef.current = new SubscriptionExplorer(
        subscriptionClientRef.current.getWebSocketClient(),
      );

      // Set up connection state listener BEFORE connecting
      // This ensures we catch the Connected state when it happens
      subscriptionExplorerRef.current.onStateChange((state) => {
        if (state === ConnectionState.Connected) {
          setIsConnected(true);
          if (wasDisconnectedRef.current) {
            console.warn("[ExplorerTestPage] Reconnected - history may be incomplete");
            // Could request snapshot here if API exists
            wasDisconnectedRef.current = false;
          }
          addEvent(
            {
              eventType: "info",
              data: {
                id: "connection-success",
                type: "vertex",
                shardId: 0,
                timestamp: Math.floor(Date.now() / 1000),
                status: "connected",
                originalProtoHash: "",
              },
              sourceShard: "system",
              eventTime: Math.floor(Date.now() / 1000),
            },
            "success",
          );
        } else if (state === ConnectionState.Disconnected || state === ConnectionState.Reconnecting) {
          setIsConnected(false);
          setIsSubscribed(false);
          setIsSubscribedTransactions(false);
          wasDisconnectedRef.current = true;
        }
      });

      // Connect SubscriptionClient if not already connected
      // The state listener will be notified when connection is established
      if (!subscriptionClientRef.current.isConnected()) {
        await subscriptionClientRef.current.connect();
      } else {
        // If already connected, manually check and update state
        // This handles the case where connection was established before listener was set up
        const currentState = subscriptionExplorerRef.current.getState();
        if (currentState === ConnectionState.Connected) {
          setIsConnected(true);
          addEvent(
            {
              eventType: "info",
              data: {
                id: "connection-success",
                type: "vertex",
                shardId: 0,
                timestamp: Math.floor(Date.now() / 1000),
                status: "connected",
                originalProtoHash: "",
              },
              sourceShard: "system",
              eventTime: Math.floor(Date.now() / 1000),
            },
            "success",
          );
        }
      }

      // Note: SubscriptionExplorer.connect() is not needed here because it uses the same
      // WebSocketClient instance that SubscriptionClient already connected. Calling connect()
      // again would be idempotent but unnecessary since the connection is already established.
    } catch (error: any) {
      addEvent(
        {
          eventType: "error",
          data: {
            id: "connection-error",
            type: "vertex",
            shardId: 0,
            timestamp: Math.floor(Date.now() / 1000),
            status: "error",
            originalProtoHash: "",
          },
          sourceShard: "system",
          eventTime: Math.floor(Date.now() / 1000),
        },
        "error",
      );
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = () => {
    if (multiplexStreamClientRef.current) {
      multiplexStreamClientRef.current.detach();
      multiplexStreamClientRef.current = null;
      multiplexTxUnsubRef.current = null;
      multiplexTxSubsRef.current = null;
    }
    if (subscriptionExplorerRef.current) {
      try {
        subscriptionExplorerRef.current.disconnect();
      } catch (error) {
        // Ignore cleanup errors
      }
    }
    // Note: We don't disconnect SubscriptionClient here to preserve the shared connection
    // If you want to fully disconnect, you can also call:
    // subscriptionClientRef.current?.disconnect();
    setIsConnected(false);
    setIsSubscribed(false);
    setIsSubscribedTransactions(false);
    addEvent(
      {
        eventType: "info",
        data: {
          id: "disconnected",
          type: "vertex",
          shardId: 0,
          timestamp: Math.floor(Date.now() / 1000),
          status: "disconnected",
          originalProtoHash: "",
        },
        sourceShard: "system",
        eventTime: Math.floor(Date.now() / 1000),
      },
      "info",
    );
  };

  const subscribeConsensusFlat = () => {
    if (!subscriptionExplorerRef.current?.isConnected()) {
      addEvent(
        {
          eventType: "error",
          data: {
            id: "subscription-error",
            type: "vertex",
            shardId: 0,
            timestamp: Math.floor(Date.now() / 1000),
            status: "error",
            originalProtoHash: "",
          },
          sourceShard: "system",
          eventTime: Math.floor(Date.now() / 1000),
        },
        "error",
      );
      return;
    }

    const callback = (data: ConsensusEventFlat) => {
      addEvent(data, "success", "consensus_flat");
    };

    try {
      const params: {
        shard_id?: string;
        event_type?: string;
      } = {};

      if (shardId) {
        params.shard_id = shardId;
      }
      if (eventType) {
        params.event_type = eventType;
      }

      subscriptionExplorerRef.current.subscribeConsensusFlat(
        callback,
        Object.keys(params).length > 0 ? params : undefined,
      );
      setIsSubscribed(true);
      addEvent(
        {
          eventType: "info",
          data: {
            id: "subscription-success",
            type: "vertex",
            shardId: 0,
            timestamp: Math.floor(Date.now() / 1000),
            status: "subscribed",
            originalProtoHash: "",
          },
          sourceShard: "system",
          eventTime: Math.floor(Date.now() / 1000),
        },
        "success",
        "consensus_flat",
      );
    } catch (error: any) {
      addEvent(
        {
          eventType: "error",
          data: {
            id: "subscription-error",
            type: "vertex",
            shardId: 0,
            timestamp: Math.floor(Date.now() / 1000),
            status: "error",
            originalProtoHash: "",
          },
          sourceShard: "system",
          eventTime: Math.floor(Date.now() / 1000),
        },
        "error",
        "consensus_flat",
      );
    }
  };

  const unsubscribeConsensusFlat = () => {
    if (!subscriptionExplorerRef.current?.isConnected()) {
      return;
    }

    try {
      subscriptionExplorerRef.current.unsubscribeConsensusFlat();
      setIsSubscribed(false);
      addEvent(
        {
          eventType: "info",
          data: {
            id: "unsubscribed",
            type: "vertex",
            shardId: 0,
            timestamp: Math.floor(Date.now() / 1000),
            status: "unsubscribed",
            originalProtoHash: "",
          },
          sourceShard: "system",
          eventTime: Math.floor(Date.now() / 1000),
        },
        "info",
        "consensus_flat",
      );
    } catch (error: any) {
      addEvent(
        {
          eventType: "error",
          data: {
            id: "unsubscribe-error",
            type: "vertex",
            shardId: 0,
            timestamp: Math.floor(Date.now() / 1000),
            status: "error",
            originalProtoHash: "",
          },
          sourceShard: "system",
          eventTime: Math.floor(Date.now() / 1000),
        },
        "error",
        "consensus_flat",
      );
    }
  };

  const subscribeTransactions = async () => {
    if (!subscriptionExplorerRef.current?.isConnected()) {
      addEvent(
        { eventType: "error", data: { hash: "", status: "error" }, sourceShard: "system", eventTime: Math.floor(Date.now() / 1000) },
        "error",
        "transactions",
      );
      return;
    }

    try {
      let client = multiplexStreamClientRef.current;
      if (!client) {
        client = new MultiplexStreamClient(subscriptionExplorerRef.current.getWebSocketClient());
        await client.subscribeToChannel();
        multiplexStreamClientRef.current = client;
      }

      const filter: { tx_types?: string[]; shardId?: string } = {};
      if (txTypes) {
        filter.tx_types = txTypes.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (shardId) {
        filter.shardId = shardId;
      }
      const subs: MultiplexStreamSubscription[] = [
        { stream_type: "transactions", filter: Object.keys(filter).length ? filter : undefined },
      ];
      multiplexTxUnsubRef.current = client.onStreamData("transactions", (resp) => {
        addEvent((resp.data ?? resp) as TransactionEvent, "success", "transactions");
      });
      multiplexTxSubsRef.current = subs;
      await client.subscribeStreams(subs);
      setIsSubscribedTransactions(true);
      addEvent(
        { eventType: "info", data: { hash: "subscription-success", status: "subscribed" }, sourceShard: "system", eventTime: Math.floor(Date.now() / 1000) },
        "success",
        "transactions",
      );
    } catch (error: any) {
      addEvent(
        { eventType: "error", data: { hash: "subscription-error", status: "error" }, sourceShard: "system", eventTime: Math.floor(Date.now() / 1000) },
        "error",
        "transactions",
      );
    }
  };

  const unsubscribeTransactions = () => {
    multiplexTxUnsubRef.current?.();
    if (multiplexTxSubsRef.current && multiplexStreamClientRef.current) {
      multiplexStreamClientRef.current.unsubscribeStreams(multiplexTxSubsRef.current);
    }
    multiplexTxUnsubRef.current = null;
    multiplexTxSubsRef.current = null;
    setIsSubscribedTransactions(false);
    addEvent(
      { eventType: "info", data: { hash: "unsubscribed", status: "unsubscribed" }, sourceShard: "system", eventTime: Math.floor(Date.now() / 1000) },
      "info",
      "transactions",
    );
  };

  const clearEvents = () => {
    setEvents([]);
    setDagNodes([]);
    clearDagState(dagState);
    setDagState(createDagState());
    setSelectedTxId(null);
    processedEventIdsRef.current.clear();
  };

  // Toggle handlers for consolidated buttons
  const toggleConnection = () => {
    if (isConnected) {
      disconnect();
    } else {
      connect();
    }
  };

  const toggleConsensusSubscription = () => {
    if (isSubscribed) {
      unsubscribeConsensusFlat();
    } else {
      subscribeConsensusFlat();
    }
  };

  const toggleTransactionsSubscription = () => {
    if (isSubscribedTransactions) {
      unsubscribeTransactions();
    } else {
      subscribeTransactions();
    }
  };

  // Timeline setup
  useEffect(() => {
    if (viewMode !== "timeline") {
      // Clean up timeline when switching away
      if (timelineInstanceRef.current) {
        timelineInstanceRef.current.destroy();
        timelineInstanceRef.current = null;
      }
      return;
    }

    if (!timelineRef.current || dagNodes.length === 0) {
      return;
    }

    // Destroy existing timeline
    if (timelineInstanceRef.current) {
      timelineInstanceRef.current.destroy();
      timelineInstanceRef.current = null;
    }

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      if (!timelineRef.current) return;

      const timelineItems = buildTimelineData(dagNodes);
      const groups = Array.from(new Set(timelineItems.map((i) => i.group || "root"))).map(
        (g) => ({ id: g, content: g }),
      );

      const options = {
        height: "500px",
        zoomable: true,
        moveable: true,
        selectable: true,
        onSelect: (props: any) => {
          if (props.items && props.items.length > 0) {
            setSelectedTxId(props.items[0]);
          }
        },
      };

      timelineInstanceRef.current = new Timeline(
        timelineRef.current,
        timelineItems,
        groups,
        options,
      );
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      if (timelineInstanceRef.current) {
        timelineInstanceRef.current.destroy();
        timelineInstanceRef.current = null;
      }
    };
  }, [viewMode, dagNodes]);

  // Graph container width state for responsive sizing
  const [graphWidth, setGraphWidth] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth - 100;
    }
    return 800;
  });

  // Update graph width on window resize
  useEffect(() => {
    if (viewMode !== "graph") return;

    const handleResize = () => {
      setGraphWidth(window.innerWidth - 100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [viewMode]);

  // Memoized graph data
  const graphData = useMemo(() => {
    if (viewMode !== "graph") return { nodes: [], links: [] };
    return buildGraphData(dagNodes, selectedTxId || undefined, 5);
  }, [viewMode, dagNodes, selectedTxId]);

  // Selected node details
  const selectedNode = useMemo(() => {
    if (!selectedTxId) return null;
    return dagNodes.find((tx) => tx.id === selectedTxId);
  }, [selectedTxId, dagNodes]);

  return (
    <div className="explorer-test-page">
      <div className="explorer-test-header">
        <h2>Consensus Explorer Test</h2>
        <p>
          Test the consensus_flat WebSocket channel to view flattened consensus
          events from Morpheum's DAG-based consensus system.
        </p>
      </div>

      <ConfigSection
        wsURL={wsURL}
        setWsURL={setWsURL}
        authToken={authToken}
        setAuthToken={setAuthToken}
        deviceID={deviceID}
        setDeviceID={setDeviceID}
        shardId={shardId}
        setShardId={setShardId}
        eventType={eventType}
        setEventType={setEventType}
        txAddress={txAddress}
        setTxAddress={setTxAddress}
        txHash={txHash}
        setTxHash={setTxHash}
        txTypes={txTypes}
        setTxTypes={setTxTypes}
        txEventTypes={txEventTypes}
        setTxEventTypes={setTxEventTypes}
      />

      <div className="explorer-test-controls">
        <div className="control-buttons">
          <ExplorerButton
            onClick={toggleConnection}
            disabled={isConnecting}
            variant={isConnected ? "secondary" : "primary"}
          >
            {isConnecting ? "Connecting..." : isConnected ? "Disconnect" : "Connect"}
          </ExplorerButton>
          <ExplorerButton
            onClick={toggleConsensusSubscription}
            disabled={!isConnected}
            variant={isSubscribed ? "secondary" : "primary"}
          >
            {isSubscribed ? "Unsubscribe Consensus" : "Subscribe Consensus"}
          </ExplorerButton>
          <ExplorerButton
            onClick={toggleTransactionsSubscription}
            disabled={!isConnected}
            variant={isSubscribedTransactions ? "secondary" : "primary"}
          >
            {isSubscribedTransactions ? "Unsubscribe Transactions" : "Subscribe Transactions"}
          </ExplorerButton>
          <ExplorerButton onClick={clearEvents} variant="secondary">
            Clear Events
          </ExplorerButton>
        </div>
        <div className="connection-status">
          <span
            className={`status-indicator ${isConnected ? "connected" : "disconnected"}`}
          >
            {isConnected ? "● Connected" : "○ Disconnected"}
          </span>
          {isSubscribed && (
            <span className="status-indicator subscribed">
              ● Subscribed to consensus_flat
            </span>
          )}
          {isSubscribedTransactions && (
            <span className="status-indicator subscribed">
              ● Subscribed to transactions
            </span>
          )}
          <span className="event-count">Events: {events.length}</span>
          <span className="event-count">DAG Nodes: {dagNodes.length}</span>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="view-controls">
        <ExplorerButton
          onClick={() => setViewMode("list")}
          variant={viewMode === "list" ? "primary" : "secondary"}
        >
          List View
        </ExplorerButton>
        <ExplorerButton
          onClick={() => setViewMode("timeline")}
          variant={viewMode === "timeline" ? "primary" : "secondary"}
        >
          Timeline View
        </ExplorerButton>
        <ExplorerButton
          onClick={() => setViewMode("graph")}
          variant={viewMode === "graph" ? "primary" : "secondary"}
          disabled={dagNodes.length > 100 && !selectedTxId}
        >
          Graph View {dagNodes.length > 100 && !selectedTxId ? "(Select node first)" : ""}
        </ExplorerButton>
      </div>

      {/* Timeline View */}
      {viewMode === "timeline" && (
        <div className="dag-timeline-container">
          <div ref={timelineRef} style={{ height: "500px", border: "1px solid var(--border)" }} />
          {dagNodes.length === 0 && (
            <div className="empty-state">
              <p>No DAG nodes to display. Connect and subscribe to start receiving events.</p>
            </div>
          )}
        </div>
      )}

      {/* Graph View */}
      {viewMode === "graph" && (
        <div className="dag-graph-container">
          <Suspense fallback={<div className="loading-state">Loading graph...</div>}>
            {graphData.nodes.length > 0 ? (
              <ForceGraph2D
                graphData={graphData}
                nodeLabel={(node: any) => `${node.label}\nType: ${node.type}\nShard: ${node.shardId}`}
                nodeColor={(node: any) => {
                  if (node.type === "vertex") return "#3b82f6";
                  if (node.type === "tx") return "#10b981";
                  return "#f59e0b";
                }}
                linkDirectionalArrowLength={6}
                linkDirectionalArrowRelPos={1}
                linkColor={() => "rgba(255, 255, 255, 0.3)"}
                onNodeClick={(node: any) => setSelectedTxId(node.id)}
                nodeCanvasObjectMode={() => "after"}
                nodeCanvasObject={(node: any, ctx: CanvasRenderingContext2D) => {
                  if (node.id === selectedTxId) {
                    ctx.beginPath();
                    ctx.arc(node.x || 0, node.y || 0, 8, 0, 2 * Math.PI);
                    ctx.fillStyle = "#f0f63b";
                    ctx.fill();
                    ctx.strokeStyle = "#f0f63b";
                    ctx.lineWidth = 2;
                    ctx.stroke();
                  }
                }}
                height={500}
                width={graphWidth}
              />
            ) : (
              <div className="empty-state">
                <p>
                  {dagNodes.length === 0
                    ? "No DAG nodes to display. Connect and subscribe to start receiving events."
                    : dagNodes.length > 100
                      ? "Graph view requires node selection for large datasets. Click a node in timeline view first."
                      : "No graph data available."}
                </p>
              </div>
            )}
          </Suspense>
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && <ConsensusEventList events={events} />}

      {/* Details Pane */}
      {selectedTxId && selectedNode && (
        <div className="dag-details-pane">
          <h3>Node Details: {selectedTxId}</h3>
          <div className="detail-section">
            <div className="detail-row">
              <span className="detail-label">Type:</span>
              <span className="detail-value">{selectedNode.nodeType}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Timestamp:</span>
              <span className="detail-value">
                {new Date(selectedNode.timestamp * 1000).toLocaleString()}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Shard ID:</span>
              <span className="detail-value">{selectedNode.shardId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Parents:</span>
              <span className="detail-value">{selectedNode.parents.length}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Children:</span>
              <span className="detail-value">{selectedNode.children.length}</span>
            </div>
            <details className="raw-data-details">
              <summary>View Raw Data</summary>
              <pre className="raw-data-pre">
                {JSON.stringify(selectedNode.data, null, 2)}
              </pre>
            </details>
          </div>
          <div style={{ marginTop: "10px" }}>
            <ExplorerButton
              onClick={() => setSelectedTxId(null)}
              variant="secondary"
            >
              Close Details
            </ExplorerButton>
          </div>
        </div>
      )}
    </div>
  );
}

